import { useAppDispatch } from '../../../../../store';
import { fetchShiftTimes, updateLocationSettingsHistoricalHours } from '../../../../../store/thunks/dloThunks';
import { TableHandlers, ModalState } from '../types';

interface UseDLOLocationHandlersProps {
  selectedLocation: any;
  setModalState: (modal: ModalState) => void;
}

export const useDLOLocationHandlers = ({
  selectedLocation,
  setModalState,
}: UseDLOLocationHandlersProps): TableHandlers => {
  const dispatch = useAppDispatch();

  // Retry function for shift times
  const retryShiftTimes = () => {
    dispatch(fetchShiftTimes());
  };

  // Historical Hours save handlers
  const handleSaveHistoricalHours = async (tableData?: any[]) => {
    try {
      console.log('Save Historical Hours called with tableData:', tableData);
      
      // Validate location is selected
      if (!selectedLocation?.id) {
        setModalState({
          visible: true,
          title: 'Error',
          message: 'Please select a location before saving.',
          type: 'error'
        });
        return;
      }

      // Use passed table data if available
      const dataToSave = tableData || [];
      
      // Transform data to API format
      const dayMap = {
        sun: 1, mon: 2, tue: 3, wed: 4, thu: 5, fri: 6, sat: 7
      };
      
      // For Global Save: Send ALL zones, ALL days with their current fixed_hours values
      // This includes both user-modified values and original display values (historical_hours copied to fixed_hours)
      const updates: any[] = [];
      
      dataToSave.forEach((row: any) => {
        // Send all 7 days for every zone with their current fixed_hours values
        Object.entries(dayMap).forEach(([day, dayNum]) => {
          const fixedHours = parseFloat(row.fixedHours?.[day] || '0');
          updates.push({
            vpZone: row.zone,
            dayNum,
            fixedHours
          });
        });
      });
      
      console.log('Transformed updates for API:', updates);
      
      if (updates.length === 0) {
        setModalState({
          visible: true,
          title: 'Info',
          message: 'No historical hours data to save.',
          type: 'error'
        });
        return;
      }

      // Dispatch the update
      console.log('Dispatching updateLocationSettingsHistoricalHours...');
      const result = await dispatch(updateLocationSettingsHistoricalHours({ 
        updates 
      })).unwrap();

      console.log('Save successful:', result);
      
      // Show success modal
      setModalState({
        visible: true,
        title: 'Success',
        message: 'Fixed Hours data saved successfully!',
        type: 'success'
      });

    } catch (error: any) {
      console.error('Error saving Historical Hours:', error);
      setModalState({
        visible: true,
        title: 'Error',
        message: error?.message || 'Failed to save Historical Hours data. Please try again.',
        type: 'error'
      });
    }
  };

  const handleSaveHistoricalHoursRow = async (rowId: string, tableData?: any[]) => {
    try {
      // Validate location is selected
      if (!selectedLocation?.id) {
        setModalState({
          visible: true,
          title: 'Error',
          message: 'Please select a location before saving.',
          type: 'error'
        });
        return;
      }

      // Use passed table data if available
      const dataToSave = tableData || [];
      
      // Find the specific row in table data
      const targetRow = dataToSave.find((row: any) => 
        row.id === rowId || row.zone === rowId
      );

      if (!targetRow) {
        setModalState({
          visible: true,
          title: 'Info',
          message: 'No data to save for this row.',
          type: 'error'
        });
        return;
      }

      // Transform single row data to API format
      const dayMap = {
        sun: 1, mon: 2, tue: 3, wed: 4, thu: 5, fri: 6, sat: 7
      };
      
      const updates = Object.entries(dayMap).map(([day, dayNum]) => ({
        vpZone: targetRow.zone,
        dayNum,
        fixedHours: parseFloat(targetRow.fixedHours?.[day] || '0')
      }));

      // Dispatch the update
      const result = await dispatch(updateLocationSettingsHistoricalHours({ 
        updates 
      })).unwrap();

      // Show success modal
      setModalState({
        visible: true,
        title: 'Success',
        message: 'Historical Hours row saved successfully!',
        type: 'success'
      });

    } catch (error: any) {
      console.error('Error saving Historical Hours row:', error);
      setModalState({
        visible: true,
        title: 'Error',
        message: error?.message || 'Failed to save Historical Hours row. Please try again.',
        type: 'error'
      });
    }
  };

  const handleClearHistoricalHoursRow = (rowId: string) => {
    console.log('Clearing Historical Hours row in Location Settings:', rowId);
    // TODO: Implement row-level clear for Historical Hours in Location Settings
  };

  const handleRestoreHistoricalHoursRow = (rowId: string) => {
    console.log('Restoring Historical Hours row in Location Settings:', rowId);
    // TODO: Implement row-level restore for Historical Hours in Location Settings
  };

  const handleAddZoneHistoricalHours = () => {
    console.log('Adding new zone to Historical Hours in Location Settings');
    // TODO: Implement add zone functionality for Historical Hours in Location Settings
  };

  const handleZoneAddedHistoricalHours = (zone: string) => {
    console.log('Zone added to Historical Hours in Location Settings:', zone);
    // TODO: Implement zone added callback for Historical Hours in Location Settings
  };

  return {
    handleSaveHistoricalHours,
    handleSaveHistoricalHoursRow,
    handleClearHistoricalHoursRow,
    handleRestoreHistoricalHoursRow,
    handleAddZoneHistoricalHours,
    handleZoneAddedHistoricalHours,
    retryShiftTimes
  };
};
